import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { AddFormFieldButton } from '@/components/Buttons/AddFormFieldButton/AddFormFieldButton';
import { getCollection } from '@/core/dcr/fields/registry';
import type { FieldDef } from '@/core/dcr/fields/types';
import { buildEmptyItem } from '@/core/dcr/serialize/defaults';

import { useDcrFormContext } from '../_context/DcrFormContext';
import { useFieldLabel, useTranslateLabel } from '../_hooks/useFieldLabel';
import { flashElement } from '../DcrField/DcrField';

import { CollectionCard } from './CollectionCard';
import { collectionSectionStyles as sx } from './Collections.styles';

type Props = {
	name: string;
	def: FieldDef;
};

export const CollectionSection = ({ name, def }: Props) => {
	const { countryId, picklists, disclosure } = useDcrFormContext();
	const fieldLabel = useFieldLabel();
	const translate = useTranslateLabel();
	const { control } = useFormContext();
	const { fields, append, remove } = useFieldArray({
		control,
		name,
		keyName: 'key'
	});

	const collection = def.collection ? getCollection(def.collection) : undefined;
	const lastCardRef = useRef<HTMLDivElement>(null);
	const flashNextCard = useRef(false);

	const handleAdd = useCallback(() => {
		if (!collection) return;

		append(buildEmptyItem(collection.id, { countryId, picklists }), {
			shouldFocus: false
		});
	}, [append, collection, countryId, picklists]);

	useEffect(() => {
		if (!disclosure.takeFlash(name) || fields.length > 0) return;

		flashNextCard.current = true;
		handleAdd();
	}, [disclosure, fields.length, handleAdd, name]);

	useEffect(() => {
		if (!flashNextCard.current || !lastCardRef.current) return;

		flashNextCard.current = false;
		flashElement(lastCardRef.current);
	}, [fields.length]);

	if (!collection) return null;

	return (
		<Box sx={sx.root}>
			<Typography variant='body2' sx={sx.label}>
				{fieldLabel(def)}
			</Typography>

			<Box sx={sx.fieldWrapper}>
				{fields.length > 0 && (
					<Box sx={sx.cardsContainer}>
						{fields.map((field, index) => (
							<CollectionCard
								key={field.key}
								collectionKey={name}
								collection={collection}
								index={index}
								isLastItem={index === fields.length - 1}
								onRemove={() => remove(index)}
								cardRef={index === fields.length - 1 ? lastCardRef : undefined}
							/>
						))}
					</Box>
				)}

				<Box sx={sx.addButton}>
					<AddFormFieldButton
						label={translate(collection.addLabel)}
						onClick={handleAdd}
					/>
				</Box>
			</Box>
		</Box>
	);
};
